import type {
  AdjustmentInput,
  AdminBookingView,
  BookingRefundInput,
  CashAccountInput,
  CashAccountView,
  CashFlowQuery,
  CashFlowReport,
  CreditNoteInput,
  ExchangeRateInput,
  ExchangeRatesOverview,
  ExpenseCategoryInput,
  ExpenseCategoryView,
  ExpenseInput,
  ExpenseView,
  ExportFormat,
  FinanceExport,
  FinanceOverview,
  FinancePaymentItem,
  FinanceReference,
  FinanceRefundItem,
  InvoiceFromBookingInput,
  InvoiceInput,
  InvoiceListItem,
  InvoiceView,
  LedgerEntryView,
  ManualPaymentInput,
  Paginated,
  PaymentLinkInput,
  ProfitabilityQuery,
  ProfitabilityReport,
  ReceivableItem,
  TransferInput,
} from '@duvamarine/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from './api';

/* Finance screens (Phase 3) and the booking desk's money actions. */

const FINANCE = ['admin', 'finance'] as const;

type Params = Record<string, string | number | boolean | null | undefined>;

function useLanguage(): string {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage ?? 'tr';
}

/** Query string from filters, dropping empty values. */
export function query(params: Params): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

function useRefreshFinance() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: FINANCE });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'booking'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
  };
}

// ---------- Reference & overview ----------

export function useFinanceReference() {
  const language = useLanguage();
  return useQuery({
    queryKey: [...FINANCE, 'reference', language],
    queryFn: () => api.get<FinanceReference>('/admin/finance/reference'),
    staleTime: 5 * 60_000,
  });
}

export function useFinanceOverview() {
  return useQuery({
    queryKey: [...FINANCE, 'overview'],
    queryFn: () => api.get<FinanceOverview>('/admin/finance/overview'),
  });
}

// ---------- Lists ----------

function useList<T>(path: string, filters: Params) {
  const language = useLanguage();
  const qs = query(filters);
  return useQuery({
    queryKey: [...FINANCE, path, qs, language],
    queryFn: () => api.get<Paginated<T>>(`/admin/finance/${path}${qs}`),
    placeholderData: keepPreviousData,
  });
}

export const usePayments = (filters: Params) => useList<FinancePaymentItem>('payments', filters);
export const useRefunds = (filters: Params) => useList<FinanceRefundItem>('refunds', filters);
export const useReceivables = (filters: Params) => useList<ReceivableItem>('receivables', filters);
export const useLedger = (filters: Params) => useList<LedgerEntryView>('ledger', filters);
export const useInvoices = (filters: Params) => useList<InvoiceListItem>('invoices', filters);
export const useExpenses = (filters: Params) => useList<ExpenseView>('expenses', filters);

export function useRetryRefund() {
  const refresh = useRefreshFinance();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/admin/finance/refunds/${id}/retry`, {}),
    onSuccess: refresh,
  });
}

// ---------- Accounts & ledger ----------

export function useCashAccounts() {
  return useQuery({
    queryKey: [...FINANCE, 'accounts'],
    queryFn: () => api.get<CashAccountView[]>('/admin/finance/accounts'),
  });
}

export function useAccountMutations() {
  const refresh = useRefreshFinance();
  return {
    save: useMutation({
      mutationFn: ({ id, input }: { id: string | null; input: CashAccountInput }) =>
        id
          ? api.put<void>(`/admin/finance/accounts/${id}`, input)
          : api.post<void>('/admin/finance/accounts', input),
      onSuccess: refresh,
    }),
    transfer: useMutation({
      mutationFn: (input: TransferInput) => api.post<void>('/admin/finance/transfers', input),
      onSuccess: refresh,
    }),
    adjust: useMutation({
      mutationFn: (input: AdjustmentInput) => api.post<void>('/admin/finance/adjustments', input),
      onSuccess: refresh,
    }),
    reverse: useMutation({
      mutationFn: ({ id, reason }: { id: string; reason: string }) =>
        api.post<void>(`/admin/finance/ledger/${id}/reverse`, { reason }),
      onSuccess: refresh,
    }),
  };
}

// ---------- Expenses ----------

export function useExpenseCategories() {
  return useQuery({
    queryKey: [...FINANCE, 'expense-categories'],
    queryFn: () => api.get<ExpenseCategoryView[]>('/admin/finance/expense-categories'),
  });
}

export function useExpenseMutations() {
  const refresh = useRefreshFinance();
  return {
    save: useMutation({
      mutationFn: ({ id, input }: { id: string | null; input: ExpenseInput }) =>
        id
          ? api.put<ExpenseView>(`/admin/finance/expenses/${id}`, input)
          : api.post<ExpenseView>('/admin/finance/expenses', input),
      onSuccess: refresh,
    }),
    cancel: useMutation({
      mutationFn: (id: string) => api.post<ExpenseView>(`/admin/finance/expenses/${id}/cancel`, {}),
      onSuccess: refresh,
    }),
    receipt: useMutation({
      mutationFn: ({ id, file }: { id: string; file: File }) => {
        const body = new FormData();
        body.append('file', file);
        return api.post<ExpenseView>(`/admin/finance/expenses/${id}/receipt`, body);
      },
      onSuccess: refresh,
    }),
    saveCategory: useMutation({
      mutationFn: ({ id, input }: { id: string | null; input: ExpenseCategoryInput }) =>
        id
          ? api.put<void>(`/admin/finance/expense-categories/${id}`, input)
          : api.post<void>('/admin/finance/expense-categories', input),
      onSuccess: refresh,
    }),
  };
}

// ---------- Invoices ----------

export function useInvoice(id: string | null) {
  return useQuery({
    queryKey: [...FINANCE, 'invoice', id],
    queryFn: () => api.get<InvoiceView>(`/admin/finance/invoices/${id ?? ''}`),
    enabled: !!id,
  });
}

export function useInvoiceMutations() {
  const queryClient = useQueryClient();
  const refresh = useRefreshFinance();
  const update = (invoice: InvoiceView) => {
    queryClient.setQueryData([...FINANCE, 'invoice', invoice.id], invoice);
    refresh();
  };
  return {
    save: useMutation({
      mutationFn: ({ id, input }: { id: string | null; input: InvoiceInput }) =>
        id
          ? api.put<InvoiceView>(`/admin/finance/invoices/${id}`, input)
          : api.post<InvoiceView>('/admin/finance/invoices', input),
      onSuccess: update,
    }),
    fromBooking: useMutation({
      mutationFn: ({ bookingId, input }: { bookingId: string; input: InvoiceFromBookingInput }) =>
        api.post<InvoiceView>(`/admin/finance/invoices/from-booking/${bookingId}`, input),
      onSuccess: update,
    }),
    issue: useMutation({
      mutationFn: (id: string) => api.post<InvoiceView>(`/admin/finance/invoices/${id}/issue`, {}),
      onSuccess: update,
    }),
    send: useMutation({
      mutationFn: ({ id, to }: { id: string; to: string | null }) =>
        api.post<InvoiceView>(`/admin/finance/invoices/${id}/send`, { to }),
      onSuccess: update,
    }),
    cancel: useMutation({
      mutationFn: ({ id, reason }: { id: string; reason: string }) =>
        api.post<InvoiceView>(`/admin/finance/invoices/${id}/cancel`, { reason }),
      onSuccess: update,
    }),
    credit: useMutation({
      mutationFn: ({ id, input }: { id: string; input: CreditNoteInput }) =>
        api.post<InvoiceView>(`/admin/finance/invoices/${id}/credit-notes`, input),
      onSuccess: update,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<void>(`/admin/finance/invoices/${id}`),
      onSuccess: refresh,
    }),
  };
}

// ---------- Reports & rates ----------

export function useCashFlow(params: CashFlowQuery, enabled = true) {
  return useQuery({
    queryKey: [...FINANCE, 'cash-flow', params],
    enabled,
    queryFn: () =>
      api.get<CashFlowReport>(`/admin/finance/reports/cash-flow${query({ ...params })}`),
    placeholderData: keepPreviousData,
  });
}

export function useProfitability(params: ProfitabilityQuery, enabled = true) {
  const language = useLanguage();
  return useQuery({
    queryKey: [...FINANCE, 'profitability', params, language],
    enabled,
    queryFn: () =>
      api.get<ProfitabilityReport>(`/admin/finance/reports/profitability${query({ ...params })}`),
    placeholderData: keepPreviousData,
  });
}

export function useRates() {
  return useQuery({
    queryKey: [...FINANCE, 'rates'],
    queryFn: () => api.get<ExchangeRatesOverview>('/admin/finance/rates'),
  });
}

export function useRateMutations() {
  const refresh = useRefreshFinance();
  return {
    save: useMutation({
      mutationFn: (input: ExchangeRateInput) => api.post<void>('/admin/finance/rates', input),
      onSuccess: refresh,
    }),
    refresh: useMutation({
      mutationFn: () =>
        api.post<{ date: string; count: number }>('/admin/finance/rates/refresh', {}),
      onSuccess: refresh,
    }),
  };
}

// ---------- Booking desk money actions ----------

export function useBookingMoney(bookingId: string) {
  const queryClient = useQueryClient();
  const refresh = useRefreshFinance();
  const language = useLanguage();
  const update = (view: AdminBookingView) => {
    queryClient.setQueryData(['admin', 'booking', bookingId, language], view);
    refresh();
  };
  return {
    collect: useMutation({
      mutationFn: (input: ManualPaymentInput) =>
        api.post<AdminBookingView>(`/admin/bookings/${bookingId}/payments`, input),
      onSuccess: update,
    }),
    refund: useMutation({
      mutationFn: (input: BookingRefundInput) =>
        api.post<AdminBookingView>(`/admin/bookings/${bookingId}/refunds`, input),
      onSuccess: update,
    }),
    retry: useMutation({
      mutationFn: (refundId: string) =>
        api.post<AdminBookingView>(`/admin/bookings/${bookingId}/refunds/${refundId}/retry`, {}),
      onSuccess: update,
    }),
    paymentLink: useMutation({
      mutationFn: (input: PaymentLinkInput) =>
        api.post<void>(`/admin/bookings/${bookingId}/payment-link`, input),
    }),
  };
}

// ---------- Files ----------

export async function downloadFile(path: string, filename: string): Promise<void> {
  const blob = await api.get<Blob>(path, { responseType: 'blob' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function exportFile(kind: FinanceExport, format: ExportFormat, filters: Params) {
  const stamp = new Date().toISOString().slice(0, 10);
  return downloadFile(
    `/admin/finance/exports/${kind}${query({ ...filters, format })}`,
    `duvamarine-${kind}-${stamp}.${format}`,
  );
}

/** Opens a private file (receipt) in a new tab. */
export async function openFile(path: string): Promise<void> {
  const blob = await api.get<Blob>(path, { responseType: 'blob' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
